import { Alert, DialogContent, DialogContentText, Grid } from "@mui/material";
import Popup from "../../../components/common/model/Popup";
import useStyles from "../../../components/common/model/styles";
import React, { useEffect, useState } from "react";
import closeIcon from "../../../assets/closeModel.png";
import InvoiceForm from "./credit/InvoiceForm";
import InvoiceVatForm from "./credit/InvoiceVatForm";
import InvoiceAction from "./InvoiceAction";
import QuantityTable from "./QuantityTable";
import PaymentInvoiceTable from "./PaymentInvoiceTable";
import Notification from "../../../components/common/notification/Notification";
import { useDispatch } from "react-redux";
import { setNotificationAction } from "../../../store/reducers/mmymSlice";
import {
  getInvoiceListByCustomerIdAction,
  getSingleInvoiceAction,
  initialCreditInvoiceLocalState,
  setAddInvoiceResponse,
  setSingleInvoiceAction,
} from "../../../store/reducers/invoiceSlice";
import { useSelector } from "react-redux";
import {
  setAddPaymentResponse,
  setAllPaymentListAction,
  setPaymentDeletedResponse,
} from "../../../store/reducers/paymentSlice";
import {
  getInvoiceTypeListAction,
  getPaymentMethodListAction,
} from "../../../store/reducers/quickBookSlice";
import AlertPopup from "../../../components/common/alertPopup/AlertPopup";

interface ManageCreditInvoiceProps {
  open: boolean; // Define the 'open' prop
  onHide: () => void; // Define the 'onHide' prop
  title: string; // Define the 'title' prop
  selectedLead?: any; // Adjust type as per your requirement
  selectedInvoice?: any; // Adjust type as per your requirement
  invoiceType?: any;
  isFormReadyOnly?: any;
  isNewInvoice?: boolean; // Optional prop to indicate if it's a new invoice
  isSetLeadDetails?: boolean; // Optional prop to indicate if lead details should be set
  isCompletionInvoice?: boolean; // Optional prop to indicate if it's from completion
  selectedCustomerId?: string; // Optional prop for selected customer ID
}

const ManageCreditInvoice: React.FC<ManageCreditInvoiceProps> = (props) => {
  const classes = useStyles();
  const {
    onHide,
    open,
    title,
    selectedInvoice,
    invoiceType,
    isNewInvoice,
    selectedCustomerId,
    isFormReadyOnly = false,
    isSetLeadDetails = false, // Optional prop to indicate if lead details should be set
    isCompletionInvoice = false, // Optional prop to indicate if it's from completion
  } = props;

  const { invoiceDetails, isAddInvoiceResponse } = useSelector(
    (state: any) => state.invoice
  );

  const { isAddPaymentResponse, isPaymentDeletedResponse } = useSelector(
    (state: any) => state.payment
  );
  const { leadDetails } = useSelector((state: any) => state.lead);

  const [formData, setFormData] = useState(
    isNewInvoice ? initialCreditInvoiceLocalState : null
  );
  const [isDisabled, setIsDisabled] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    if (open) {
      dispatch(
        getInvoiceListByCustomerIdAction({
          customerId: selectedCustomerId || leadDetails?.lead?.generated_id || "",
        })
      );
    }
  }, [open]);

  useEffect(() => {
    dispatch(getInvoiceTypeListAction());
    dispatch(getPaymentMethodListAction());
  }, []);

  useEffect(() => {
    if (!isNewInvoice) {
      const payload: any = {};
      if (selectedInvoice) {
        payload.invoiceNo = selectedInvoice;
      } else {
        payload.leadNo = leadDetails?.lead?.generated_id;
        payload.invoiceType = invoiceType;
      }
      dispatch(getSingleInvoiceAction(payload));
      dispatch(setAddInvoiceResponse(false));
      dispatch(setAddPaymentResponse(false));
      dispatch(setPaymentDeletedResponse(false));
      dispatch(setSingleInvoiceAction(null));
      setIsDisabled(true);
    } else if (isAddInvoiceResponse && isNewInvoice) {
      dispatch(setAddInvoiceResponse(false));
      onHide();
    }
  }, [
    selectedInvoice,
    leadDetails,
    isAddInvoiceResponse,
    isAddPaymentResponse,
    isPaymentDeletedResponse,
  ]);

  // This effect ONLY runs when leadDetails are loaded AND it's a new invoice
  useEffect(() => {
    if (leadDetails?.lead?.generated_id && isNewInvoice && isSetLeadDetails) {
      const payload = {
        ...initialCreditInvoiceLocalState,
        customer_id: leadDetails?.lead?.customer_id,
        lead_id: leadDetails?.lead?.generated_id,
        customer_name: leadDetails?.lead?.customer_name,
        customer_email: leadDetails?.lead?.customer_email,
        address: {
          building_number:
            leadDetails?.lead?.collection_address?.[0]?.building_number || "",
          street: leadDetails?.lead?.collection_address?.[0]?.street || "",
          town: leadDetails?.lead?.collection_address?.[0]?.town || "",
          county: leadDetails?.lead?.collection_address?.[0]?.county || "",
          postcode: leadDetails?.lead?.collection_address?.[0]?.postcode || "",
          country: leadDetails?.lead?.collection_address?.[0]?.country || "",
        },
      };
      setFormData(payload);
    }
  }, [leadDetails, isNewInvoice, open]);

  useEffect(() => {
    if (invoiceDetails?.invoice_number) {
      setFormData(invoiceDetails);
    } else if (!isNewInvoice && invoiceDetails?.invoice_type) {
      setFormData(invoiceDetails);
    } else if (isCompletionInvoice && invoiceDetails?.invoice_type) {
      setFormData(invoiceDetails);
    }
  }, [invoiceDetails]);

  const hideHandler = () => {
    onHide();
    dispatch(setAddInvoiceResponse(false));
    dispatch(setSingleInvoiceAction(null));
    dispatch(setNotificationAction({ message: [], severity: "error" }));
    dispatch(
      setAllPaymentListAction({ pagination: { currentPage: 1 }, list: [] })
    );
    dispatch(setAddPaymentResponse(true));
    setFormData(null);
  };

  const [paymentTableData, setPaymentTableData] = useState<
    typeof formData | null
  >(null);

  const handleTriggerPaymentAction = () => {
    setPaymentTableData(formData || null);
  };

  const [isAlertOpen, setIsAlertOpen] = useState(
    isCompletionInvoice === false && isNewInvoice ? true : false
  );

  return (
    <>
      <Popup
        open={open} // Example value, replace with your actual state or variable
        title={title} // Example title, replace with your actual title
        onHide={hideHandler}
        fullWidth={true}
        maxWidth={"lg"}
        aria-describedby="alert-dialog-slide-description"
        popupBg="#FFFFFF"
        header={
          <>
            <img
              onClick={hideHandler}
              src={closeIcon}
              className={classes.closeicons}
              alt="close"
            />
          </>
        }
      >
        <DialogContent sx={{ padding: "10px" }}>
          <DialogContentText>
            <>
              <Notification />
              <>
                <InvoiceForm
                  formData={formData}
                  setFormData={setFormData}
                  isFormReadyOnly={isFormReadyOnly}
                  isNewInvoice={isNewInvoice} // 👈 pass it here
                  invoiceType={invoiceType}
                />
                <QuantityTable
                  formData={formData}
                  setFormData={setFormData}
                  invoiceType={invoiceType}
                  isFormReadyOnly={isFormReadyOnly}
                  isNewInvoice={isNewInvoice} // 👈 pass it here
                />
                <InvoiceVatForm
                  isNewInvoice={isNewInvoice} // 👈 pass it here
                  invoiceType={invoiceType}
                  invoiceDetails={invoiceDetails}
                  formData={formData}
                  setFormData={setFormData}
                  isFormReadyOnly={isFormReadyOnly}
                  onTriggerPayment={handleTriggerPaymentAction} // 👈 pass it here
                />
                {invoiceDetails?.is_sent_to_quickbook && (
                  <Alert severity="warning" sx={{ mb: 2, mt: 2 }}>
                    Warning: -This invoice has already been synchronised with
                    the accounts package. Any changes will not refracted.
                  </Alert>
                )}
                <InvoiceAction
                  formData={formData}
                  setFormData={setFormData}
                  isDisabled={isDisabled}
                  selectedInvoice={selectedInvoice}
                  isFormReadyOnly={isFormReadyOnly}
                  isCompletionInvoice={isCompletionInvoice} // 👈 pass it here
                />
                {invoiceDetails?.invoice_number && (
                  <PaymentInvoiceTable
                    invoiceFormData={formData}
                    selectedInvoice={selectedInvoice}
                    isFormReadyOnly={isFormReadyOnly}
                    paymentTableData={paymentTableData}
                  />
                )}
              </>
            </>
          </DialogContentText>
        </DialogContent>
      </Popup>
      {isAlertOpen && (
        <AlertPopup
          open={isAlertOpen}
          onHide={() => {
            setIsAlertOpen(false);
            hideHandler();
          }}
          // yesText="Create with Selected Customer"
          // noText="Create Blank Invoice"
          submitHandler={() => {
            setIsAlertOpen(false);
          }}
          title="Alert"
          content="Do you want to create a new invoice with the selected customer?"
        />
      )}
    </>
  );
};

export default ManageCreditInvoice;
